import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { GraphEdge, GraphKind, GraphNode } from '@/lib/apiTypes'
import type { BlastRadius } from '@/features/graph/lib/graphView'
import { graphNodeHref } from '@/features/graph/lib/graphLinks'
import { DeprecateModal, UndeprecateModal, BlockModal } from '@/shared/components/ModuleModals'
import { SourceRepoLink } from '@/shared/components/SourceRepoLink'
import { ChipIcon, DatabaseIcon, BoxIcon, UploadIcon } from '@/shared/components/Icons'
import '@/features/graph/DepGraph.css'
import s from '@/features/graph/components/ModuleDetailPanel.module.css'

/**
 * The graph's right-hand inspector. Kind-aware, because the graph now holds state files and
 * providers as well as modules, and each answers a different question.
 *
 * Two things it does that the previous version did not:
 *
 *  - the consumer and dependency lists are BUTTONS. They were inert text, which made the panel
 *    a dead end: the one place that knows a node's neighbours could not take you to them.
 *  - "Open module" goes somewhere real. It used to navigate to /modules/{ns}/modules/... which
 *    matches no route, so it fell through to the catch-all and bounced to the dashboard.
 */

const KIND_GLYPH: Record<GraphKind, React.ReactNode> = {
  state: <DatabaseIcon size={13} />,
  module: <BoxIcon size={13} />,
  provider: <ChipIcon size={13} />,
}

const KIND_NOUN: Record<GraphKind, string> = {
  state: 'state file',
  module: 'module',
  provider: 'provider',
}

export interface ModuleDetailPanelProps {
  node: GraphNode
  allNodes: GraphNode[]
  allEdges: GraphEdge[]
  currentOrg?: string
  blast?: BlastRadius
  inCycle?: boolean
  onClose: () => void
  onSelectNode: (node: GraphNode) => void
  onMutated: () => void
  onLockFileHelp: (node: GraphNode) => void
  navigable?: boolean
}

export function ModuleDetailPanel({
  node, allNodes, allEdges, currentOrg, blast, inCycle,
  onClose, onSelectNode, onMutated, onLockFileHelp, navigable = true,
}: ModuleDetailPanelProps) {
  const navigate = useNavigate()
  const [deprecateOpen, setDeprecateOpen] = useState(false)
  const [undeprecateOpen, setUndeprecateOpen] = useState(false)
  const [blockOpen, setBlockOpen] = useState(false)

  const isOwned = !node.external && (currentOrg ? node.namespace === currentOrg : false)
  const hasVersion = !!node.latest_version && !node.external
  const canGovern = node.kind === 'module' && isOwned && hasVersion

  const byId = useMemo(() => new Map(allNodes.map((n) => [n.id, n])), [allNodes])

  const consumers = useMemo(
    () => allEdges.filter((e) => e.to === node.id).map((e) => byId.get(e.from)).filter((n): n is GraphNode => !!n),
    [node.id, allEdges, byId],
  )
  const dependencies = useMemo(
    () => allEdges.filter((e) => e.from === node.id).map((e) => byId.get(e.to)).filter((n): n is GraphNode => !!n),
    [node.id, allEdges, byId],
  )

  function openDetail() {
    const href = graphNodeHref(node, currentOrg)
    if (href) navigate(href)
  }

  const noLockFile = node.kind === 'state' && node.has_lock_file === false

  return (
    <div className={`dep-graph-panel ${s.panel}`}>
      <div className={s.header}>
        <div className={s.headerContent}>
          <div className={s.headerTitle}>
            <span className={s.titleGlyph}>{KIND_GLYPH[node.kind]}</span>
            {node.label}
          </div>
          <div className={s.headerBadges}>
            <span className="badge badge-neutral">{KIND_NOUN[node.kind]}</span>
            {node.blocked && <span className="badge badge-red">blocked</span>}
            {!node.blocked && node.deprecated && <span className="badge badge-amber">deprecated</span>}
            {node.outdated && <span className="badge badge-amber">outdated</span>}
            {node.policy_failed && <span className="badge badge-accent">policy failed</span>}
            {node.external && <span className="badge badge-neutral">external</span>}
            {inCycle && <span className="badge badge-amber">in a cycle</span>}
          </div>
        </div>
        <button onClick={onClose} className={s.closeBtn} aria-label="Close panel">×</button>
      </div>

      <div className={s.body}>
        <span className={s.sectionLabelFirst}>// info</span>
        <div className={s.infoGrid}>
          {node.kind === 'module' && (
            hasVersion
              ? <div>{node.latest_version} · {node.version_count} version{node.version_count !== 1 ? 's' : ''}</div>
              : <div className={s.noVersions}>
                  {node.external ? '(outside this org)' : '(no published version — mid-scan, or its scan failed)'}
                </div>
          )}
          {node.kind === 'state' && (
            <div>{node.serial != null ? `serial ${node.serial}` : 'no state pushed yet'}</div>
          )}
          {node.kind === 'provider' && (
            <div>{node.discovered ? 'discovered from a lock file or module' : 'added by an admin'}</div>
          )}

          {(node.critical_count > 0 || node.high_count > 0) && (
            <div className={s.scanCounts}>
              {node.critical_count > 0 && <span className={s.criticalText}>● {node.critical_count} critical</span>}
              {node.high_count > 0 && <span className={s.highText}>● {node.high_count} high</span>}
            </div>
          )}
          {node.blocked && node.blocked_reason && (
            <div className={s.deprecatedMsg}>✗ {node.blocked_reason}</div>
          )}
          {node.deprecated && node.deprecated_message && (
            <div className={s.deprecatedMsg}>⚠ {node.deprecated_message}</div>
          )}
        </div>

        {/* Blast radius, broken down. It is the same walk that isolating upstream performs, so
            the number here and the graph you get from clicking agree by construction. */}
        {blast && blast.total > 0 && (
          <>
            <span className={s.sectionLabel}>// blast radius</span>
            <div className={s.infoGrid}>
              <div>{blast.total} depend on this, transitively</div>
              <div className={s.blastBreakdown}>
                {blast.byKind.state > 0 && <span>{blast.byKind.state} state file{blast.byKind.state === 1 ? '' : 's'}</span>}
                {blast.byKind.module > 0 && <span>{blast.byKind.module} module{blast.byKind.module === 1 ? '' : 's'}</span>}
                {blast.byKind.provider > 0 && <span>{blast.byKind.provider} provider{blast.byKind.provider === 1 ? '' : 's'}</span>}
              </div>
            </div>
          </>
        )}

        {/* A workspace with no lock file: the gap, and the way to close it. */}
        {noLockFile && (
          <>
            <span className={s.sectionLabel}>// no lock file</span>
            <div className={s.infoGrid}>
              <div className={s.noVersions}>
                Its provider pins are invisible, so it is excluded from CVE and outdated-version
                warnings.
              </div>
              {node.vcs_repository && (
                <div className={s.provenanceRow}>
                  <SourceRepoLink
                    provider={node.vcs_provider ?? null}
                    repository={node.vcs_repository}
                    gitRef={node.vcs_ref ?? null}
                    refType={node.vcs_ref_type ?? null}
                    sha={node.vcs_sha ?? null}
                    commitUrl={node.vcs_commit_url ?? null}
                    verb="pushed"
                  />
                  <span className={s.provenanceText}>{node.vcs_repository}</span>
                </div>
              )}
              <button className="btn btn-primary btn-sm" onClick={() => onLockFileHelp(node)}>
                <UploadIcon size={12} /> How to add one
              </button>
            </div>
          </>
        )}

        {(navigable || canGovern) && (
          <>
            <span className={s.sectionLabel}>// actions</span>
            <div className={s.actionRow}>
              {navigable && !node.external && (
                <button className="btn btn-ghost btn-sm" onClick={openDetail}>
                  Open {KIND_NOUN[node.kind]} →
                </button>
              )}
              {canGovern && !node.deprecated && (
                <button className={`btn btn-sm ${s.btnDeprecate}`} onClick={() => setDeprecateOpen(true)}>
                  ⚠ Deprecate
                </button>
              )}
              {canGovern && node.deprecated && (
                <button className={`btn btn-sm ${s.btnUndeprecate}`} onClick={() => setUndeprecateOpen(true)}>
                  ✓ Undeprecate
                </button>
              )}
              {canGovern && (
                <button className="btn btn-sm btn-danger" onClick={() => setBlockOpen(true)}>
                  ✗ Block
                </button>
              )}
            </div>
          </>
        )}

        <NeighbourList
          label={`consumers (${consumers.length})`}
          arrow="↑"
          hint="depend on this"
          nodes={consumers}
          onSelect={onSelectNode}
        />
        <NeighbourList
          label={`dependencies (${dependencies.length})`}
          arrow="↓"
          hint="this depends on"
          nodes={dependencies}
          onSelect={onSelectNode}
        />
      </div>

      {deprecateOpen && node.latest_version && node.module_name && (
        <DeprecateModal
          namespace={node.namespace}
          name={node.module_name}
          provider={node.module_provider ?? ''}
          fixedVersion={node.latest_version}
          latestVersion={node.latest_version}
          dependentCount={blast?.total ?? 0}
          onClose={() => setDeprecateOpen(false)}
          onDeprecated={() => { setDeprecateOpen(false); onMutated() }}
        />
      )}
      {undeprecateOpen && node.latest_version && node.module_name && (
        <UndeprecateModal
          namespace={node.namespace}
          name={node.module_name}
          provider={node.module_provider ?? ''}
          version={node.latest_version}
          onClose={() => setUndeprecateOpen(false)}
          onUndeprecated={() => { setUndeprecateOpen(false); onMutated() }}
        />
      )}
      {blockOpen && node.latest_version && node.module_name && (
        <BlockModal
          namespace={node.namespace}
          name={node.module_name}
          provider={node.module_provider ?? ''}
          version={node.latest_version}
          dependentCount={blast?.total ?? 0}
          onClose={() => setBlockOpen(false)}
          onBlocked={() => { setBlockOpen(false); onMutated() }}
        />
      )}
    </div>
  )
}

/** Clickable, so the panel is a way to walk the graph rather than a dead end. */
function NeighbourList({
  label, arrow, hint, nodes, onSelect,
}: {
  label: string
  arrow: string
  hint: string
  nodes: GraphNode[]
  onSelect: (n: GraphNode) => void
}) {
  return (
    <>
      <span className={s.sectionLabel} title={hint}>// {label}</span>
      {nodes.length === 0 ? (
        <div className={s.emptyText}>none</div>
      ) : (
        <div className={s.nodeGrid}>
          {nodes.map((n) => (
            <button key={n.id} className={s.nodeRow} onClick={() => onSelect(n)} title={`Isolate ${n.label}`}>
              <span className={s.nodeArrow}>{arrow}</span>
              <span className={s.nodeGlyph}>{KIND_GLYPH[n.kind]}</span>
              <span className={s.nodeLabel}>{n.label}</span>
              {n.blocked && <span className={s.nodeFlagRed}>✗</span>}
              {!n.blocked && (n.deprecated || n.outdated) && <span className={s.nodeFlagAmber}>⚠</span>}
            </button>
          ))}
        </div>
      )}
    </>
  )
}
